// src/composables/usePostHog.ts
import posthog from 'posthog-js'

// Singleton flag to prevent multiple initializations
let isInitialized = false

/**
 * Initialize PostHog - should be called once at app startup
 */
export function initPostHog() {
  if (isInitialized) return

  posthog.init('phc_3E51KCV4TnaM8UqkDvVvzh1me9diineplZ0iQLZztvd', {
    api_host: 'https://eu.i.posthog.com',
    capture_pageview: true, 
    capture_pageleave: true,
    persistence: 'localStorage+cookie',
    cross_subdomain_cookie: true,
    // Session recording settings
    disable_session_recording: false, // Explicitly enable session recording
    session_recording: {
      maskAllInputs: false, // Set to true if you want to mask all input values
      maskInputOptions: {
        password: true, // Always mask password fields
      },
    },
  })

  isInitialized = true
}

/**
 * PostHog composable for tracking events and user identification
 */
export function usePostHog() {
  /**
   * Identify a user with their properties
   */
  function identify(userId: string | number, properties?: Record<string, any>) {
    posthog.identify(String(userId), properties)
  }

  /**
   * Reset user identity (for logout)
   */
  function reset() {
    posthog.reset()
  }

  /**
   * Track a custom event
   */
  function trackEvent(eventName: string, properties?: Record<string, any>) {
    posthog.capture(eventName, properties)
  }

  /**
   * Track a page view
   */
  function trackPageView(pageName?: string, properties?: Record<string, any>) {
    posthog.capture('$pageview', {
      $current_url: window.location.href,
      page_name: pageName,
      ...properties,
    })
  }

  // ==================
  // Lead Form Events
  // ==================

  function trackLeadFormStarted(properties?: {
    expertSlug?: string
    packagedServiceSlug?: string
    source?: string
    referrer?: string
  }) {
    trackEvent('lead_form_started', properties)
  }

  function trackLeadFormStepViewed(step: string, stepNumber: number, totalSteps: number) {
    trackEvent('lead_form_step_viewed', {
      step_name: step,
      step_number: stepNumber,
      total_steps: totalSteps,
    })
  }

  function trackLeadFormStepCompleted(step: string, stepNumber: number, properties?: Record<string, any>) {
    trackEvent('lead_form_step_completed', {
      step_name: step,
      step_number: stepNumber,
      ...properties,
    })
  }

  function trackLeadFormBackNavigation(fromStep: string, toStep: string) {
    trackEvent('lead_form_back_navigation', {
      from_step: fromStep,
      to_step: toStep,
    })
  }

  function trackLeadFormValidationError(step: string, errors: Record<string, string>) {
    trackEvent('lead_form_validation_error', {
      step_name: step,
      errors,
    })
  }

  function trackLeadFormSubmitted(properties: {
    hasPreferredExpert: boolean
    hasPackagedService: boolean
    urgency?: string
    storeMode: 'new' | 'existing'
    matchingStrategy: 'auto' | 'selected'
  }) {
    trackEvent('lead_form_submitted', properties)
  }

  function trackLeadFormSubmitSuccess(leadId?: number) {
    trackEvent('lead_form_submit_success', { lead_id: leadId })
  }

  function trackLeadFormSubmitError(error: string) {
    trackEvent('lead_form_submit_error', { error })
  }

  function trackLeadFormAbandoned(lastStep: string) {
    trackEvent('lead_form_abandoned', { last_step: lastStep })
  }

  // ==================
  // Client Dashboard Events
  // ==================

  function trackDashboardViewed(tabName?: string) {
    trackEvent('client_dashboard_viewed', { active_tab: tabName })
  }

  function trackDashboardTabChanged(tabName: string) {
    trackEvent('client_dashboard_tab_changed', { tab_name: tabName })
  }

  function trackSubmitRequestClicked(source: string) {
    trackEvent('submit_request_clicked', { source })
  }

  function trackLeadCardClicked(leadId: number, status: string) {
    trackEvent('lead_card_clicked', { lead_id: leadId, status })
  }

  function trackReviewRequestCardClicked(requestId: number) {
    trackEvent('review_request_card_clicked', { request_id: requestId })
  }

  // ==================
  // Auth Events
  // ==================

  function trackAuthModeSelected(mode: 'login' | 'register') {
    trackEvent('lead_form_auth_mode_selected', { mode })
  }

  function trackLoginAttempt() {
    trackEvent('login_attempt', { source: 'lead_form' })
  }

  function trackLoginSuccess(userId: number) {
    trackEvent('login_success', { user_id: userId, source: 'lead_form' })
  }

  function trackLoginError(error: string) {
    trackEvent('login_error', { error, source: 'lead_form' })
  }

  function trackRegistrationAttempt() {
    trackEvent('registration_attempt', { source: 'lead_form' })
  }

  function trackRegistrationSuccess(userId: number) {
    trackEvent('registration_success', { user_id: userId, source: 'lead_form' })
  }

  function trackRegistrationError(error: string) {
    trackEvent('registration_error', { error, source: 'lead_form' })
  }

  return {
    posthog,
    identify,
    reset,
    trackEvent,
    trackPageView,
    // Lead form events
    trackLeadFormStarted,
    trackLeadFormStepViewed,
    trackLeadFormStepCompleted,
    trackLeadFormBackNavigation,
    trackLeadFormValidationError,
    trackLeadFormSubmitted,
    trackLeadFormSubmitSuccess,
    trackLeadFormSubmitError,
    trackLeadFormAbandoned,
    // Dashboard events
    trackDashboardViewed,
    trackDashboardTabChanged,
    trackSubmitRequestClicked,
    trackLeadCardClicked,
    trackReviewRequestCardClicked,
    // Auth events
    trackAuthModeSelected,
    trackLoginAttempt,
    trackLoginSuccess,
    trackLoginError,
    trackRegistrationAttempt,
    trackRegistrationSuccess,
    trackRegistrationError,
  }
}
